import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { FileText, Copy, Check, Download, Clock, Wrench, ChevronDown, ChevronRight } from "lucide-react"
import { Call, CallMessage } from "@/api/calls/calls.types"
import { cn } from "@/lib/utils"

interface TranscriptTabProps {
    call: Call
}

interface TranscriptLine {
    timestamp: string
    speaker: string
    text: string
    isToolCall?: boolean
    isToolResult?: boolean
    isJsonResult?: boolean
    rawResult?: unknown
}

// Format seconds to MM:SS or HH:MM:SS
function formatTimestamp(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    
    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// Generate transcript from messages array
function generateTranscript(messages: CallMessage[], includeTools: boolean = true): TranscriptLine[] {
    return messages
        .filter(m => m.role !== 'system') // Exclude system messages
        .filter(m => includeTools || (m.role !== 'tool_calls' && m.role !== 'tool_call_result')) // Optionally filter tool messages
        .map(message => {
            const startTime = formatTimestamp(message.secondsFromStart)
            
            // Handle tool calls
            if (message.role === 'tool_calls' && message.toolCalls) {
                const toolNames = message.toolCalls.map(tc => tc.function.name).join(', ')
                return {
                    timestamp: `[${startTime}]`,
                    speaker: '🔧 Tool Call',
                    text: toolNames,
                    isToolCall: true
                }
            }
            
            // Handle tool call results
            if (message.role === 'tool_call_result') {
                // Handle result that could be string, array, or object
                let resultText = 'No result';
                let isJsonResult = false;
                let rawResult: unknown = null;
                
                if (message.result) {
                    if (typeof message.result === 'string') {
                        resultText = message.result;
                        // Check if the string is JSON
                        try {
                            const parsed = JSON.parse(message.result);
                            if (typeof parsed === 'object') {
                                isJsonResult = true;
                                rawResult = parsed;
                            }
                        } catch {
                            // Not JSON, keep as string
                        }
                    } else {
                        // Already an object/array
                        isJsonResult = true;
                        rawResult = message.result;
                        try {
                            resultText = JSON.stringify(message.result, null, 2);
                        } catch {
                            resultText = '[Complex data]';
                            isJsonResult = false;
                        }
                    }
                }
                return {
                    timestamp: `[${startTime}]`,
                    speaker: `↳ ${message.name || 'Result'}`,
                    text: resultText,
                    isToolResult: true,
                    isJsonResult,
                    rawResult
                }
            }
            
            // Calculate end time: use duration if available, otherwise estimate ~2 seconds
            const duration = message.duration ? message.duration / 1000 : 2 // duration is in milliseconds
            const endSeconds = message.secondsFromStart + duration
            const endTime = formatTimestamp(endSeconds)
            
            return {
                timestamp: `[${startTime}-${endTime}]`,
                speaker: message.role === 'bot' ? 'Assistant' : 'User',
                text: message.message
            }
        })
}

// Component for expandable JSON result
function ExpandableJsonResult({ 
    text, 
    rawResult, 
    speaker, 
    timestamp, 
    showTimestamps 
}: { 
    text: string
    rawResult: unknown
    speaker: string
    timestamp: string
    showTimestamps: boolean
}) {
    const [expanded, setExpanded] = useState(false);
    
    // Get a preview text (first line or truncated)
    const getPreview = () => {
        if (Array.isArray(rawResult)) {
            return `[${rawResult.length} item${rawResult.length !== 1 ? 's' : ''}]`;
        }
        if (typeof rawResult === 'object' && rawResult !== null) {
            const keys = Object.keys(rawResult);
            return `{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', ...' : ''}}`;
        }
        return text.split('\n')[0].slice(0, 50) + (text.length > 50 ? '...' : '');
    };

    return (
        <div className="bg-emerald-50/50 dark:bg-emerald-950/20 -mx-2 px-2 py-1.5 rounded-md border border-emerald-200/30 dark:border-emerald-800/20">
            <div 
                className="flex gap-3 text-sm cursor-pointer hover:bg-emerald-100/30 dark:hover:bg-emerald-900/20 -mx-2 px-2 py-1 rounded transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                {showTimestamps && (
                    <span className="text-xs font-mono shrink-0 w-[70px] text-emerald-600 dark:text-emerald-400">
                        {timestamp}
                    </span>
                )}
                <span className="font-semibold shrink-0 min-w-[90px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                    {expanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                    ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                    )}
                    {speaker}:
                </span>
                <span className="text-emerald-800 dark:text-emerald-200 flex-1">
                    {expanded ? 'Click to collapse' : getPreview()}
                </span>
            </div>
            {expanded && (
                <div className="mt-2 ml-2">
                    <pre className="text-xs bg-white/50 dark:bg-black/20 rounded p-3 overflow-x-auto whitespace-pre-wrap break-words text-emerald-900 dark:text-emerald-100 border border-emerald-200/30 dark:border-emerald-800/30 max-h-[400px] overflow-y-auto">
                        {text}
                    </pre>
                </div>
            )}
        </div>
    );
}

export function TranscriptTab({ call }: TranscriptTabProps) {
    const [copied, setCopied] = useState(false)
    const [showTimestamps, setShowTimestamps] = useState(true)
    const [showToolCalls, setShowToolCalls] = useState(true)

    const formatTranscriptForExport = (): string => {
        if (!call.messages) return ''
        return generateTranscript(call.messages, showToolCalls)
            .map(line => {
                if (showTimestamps) {
                    return `${line.timestamp}  ${line.speaker}: ${line.text}`
                } else {
                    return `${line.speaker}: ${line.text}`
                }
            })
            .join('\n')
    }

    const copyToClipboard = () => {
        const formattedTranscript = formatTranscriptForExport()
        if (formattedTranscript) {
            navigator.clipboard.writeText(formattedTranscript)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    const downloadTranscript = () => {
        const formattedTranscript = formatTranscriptForExport()
        if (formattedTranscript) {
            const blob = new Blob([formattedTranscript], { type: 'text/plain' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `transcript-${call.id}.txt`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        }
    }

    // Early return if no messages
    if (!call?.messages || call.messages.length === 0) {
        return (
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Transcript</CardTitle>
                            <CardDescription className="text-xs text-muted-foreground/70">
                                No transcript available
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-base font-semibold mb-2">No Transcript Available</h3>
                        <p className="text-sm text-muted-foreground max-w-sm">
                            The transcript for this call has not been generated yet.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <FileText className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Transcript</CardTitle>
                            <CardDescription className="text-xs text-muted-foreground/70">
                                Full conversation transcript
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant={showTimestamps ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowTimestamps(!showTimestamps)}
                            className="gap-2"
                        >
                            <Clock className="h-3.5 w-3.5" />
                            Timestamps
                        </Button>
                        <Button
                            variant={showToolCalls ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowToolCalls(!showToolCalls)}
                            className="gap-2"
                        >
                            <Wrench className="h-3.5 w-3.5" />
                            Tools
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={copyToClipboard}
                            className="gap-2"
                        >
                            {copied ? (
                                <>
                                    <Check className="h-3.5 w-3.5 text-green-500" />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <Copy className="h-3.5 w-3.5" />
                                    Copy
                                </>
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={downloadTranscript}
                            className="gap-2"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Download
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-6">
                <ScrollArea className="h-[35rem] pr-4">
                    <div className="rounded-lg bg-muted/30 p-4 border space-y-3">
                        {generateTranscript(call.messages || [], showToolCalls).map((line, index) => {
                            // Use expandable component for JSON tool results
                            if (line.isToolResult && line.isJsonResult && line.rawResult) {
                                return (
                                    <ExpandableJsonResult
                                        key={index}
                                        text={line.text}
                                        rawResult={line.rawResult}
                                        speaker={line.speaker}
                                        timestamp={line.timestamp}
                                        showTimestamps={showTimestamps}
                                    />
                                );
                            }
                            
                            return (
                                <div 
                                    key={index} 
                                    className={cn(
                                        "flex gap-3 text-sm",
                                        line.isToolCall && "bg-amber-50/50 dark:bg-amber-950/20 -mx-2 px-2 py-1.5 rounded-md border border-amber-200/30 dark:border-amber-800/20",
                                        line.isToolResult && "bg-emerald-50/50 dark:bg-emerald-950/20 -mx-2 px-2 py-1.5 rounded-md border border-emerald-200/30 dark:border-emerald-800/20"
                                    )}
                                >
                                    {showTimestamps && (
                                        <span className={cn(
                                            "text-xs font-mono shrink-0",
                                            line.isToolCall || line.isToolResult ? "w-[70px]" : "w-[110px]",
                                            line.isToolCall ? "text-amber-600 dark:text-amber-400" :
                                            line.isToolResult ? "text-emerald-600 dark:text-emerald-400" :
                                            "text-muted-foreground"
                                        )}>
                                            {line.timestamp}
                                        </span>
                                    )}
                                    <span className={cn(
                                        "font-semibold shrink-0 min-w-[90px]",
                                        line.isToolCall && "text-amber-700 dark:text-amber-300",
                                        line.isToolResult && "text-emerald-700 dark:text-emerald-300"
                                    )}>
                                        {line.speaker}:
                                    </span>
                                    <p className={cn(
                                        "leading-relaxed flex-1 break-words",
                                        line.isToolCall && "text-amber-800 dark:text-amber-200 font-medium",
                                        line.isToolResult && "text-emerald-800 dark:text-emerald-200"
                                    )}>
                                        {line.text}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    )
}
